import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchHotSearchByPlatform, 
  fetchSupportedPlatforms,
  setActivePlatform,
  selectHotSearchPlatform,
  selectSupportedPlatforms,
  selectPlatformLoading,
  selectPlatformError
} from '../../../redux/slices/hotSearchSlice';
import './HotSearchContent.css';

const HotSearchContent = ({ app }) => {
  const dispatch = useDispatch();
  const [activePlatform, setActivePlatformLocal] = useState('toutiao');

  // 从 Redux 获取数据
  const platformData = useSelector(state => selectHotSearchPlatform(state, activePlatform));
  const supportedPlatforms = useSelector(selectSupportedPlatforms);
  const loading = useSelector(state => selectPlatformLoading(state, activePlatform));
  const error = useSelector(state => selectPlatformError(state, activePlatform));

  // 平台映射
  const platformMap = {
    'toutiao': '头条',
    'baidu': '百度',
    'weibo': '微博',
    'bilibili': 'B站',
    'zhihu': '知乎',
    'douyin': '抖音',
    'juejin': '掘金'
  };

  // 获取支持的平台列表
  useEffect(() => {
    if (supportedPlatforms.length === 0) {
      dispatch(fetchSupportedPlatforms());
    }
  }, [dispatch, supportedPlatforms.length]);

  // 获取当前平台数据
  useEffect(() => {
    if (activePlatform && !platformData) {
      dispatch(fetchHotSearchByPlatform({ platform: activePlatform }));
    }
  }, [dispatch, activePlatform, platformData]);

  // 处理平台切换
  const handlePlatformChange = (e, platform) => {
    e.stopPropagation();
    // 如果是重复点击同一个平台，强制刷新数据
    if (activePlatform === platform) {
      dispatch(fetchHotSearchByPlatform({ platform, refresh: true }));
    } else {
      // 切换到新平台
      setActivePlatformLocal(platform);
      dispatch(setActivePlatform(platform));
      
      // 如果该平台数据不存在，则获取数据
      if (!platformData) {
        dispatch(fetchHotSearchByPlatform({ platform }));
      }
    }
  };

  // 处理热搜项目点击
  const handleTopicClick = (e, topic) => {
    e.stopPropagation();
    let url = topic.url || topic.link || topic.href;
    
    // 如果没有直接链接，尝试构建链接
    if (!url && topic.title) {
      // 根据平台构建搜索链接
      const searchUrl = getSearchUrl(activePlatform, topic.title);
      if (searchUrl) {
        url = searchUrl;
      }
    }
    
    if (url) {
      // 在新标签页中打开链接
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // 根据平台获取搜索链接
  const getSearchUrl = (platform, title) => {
    const encodedTitle = encodeURIComponent(title);
    const searchUrls = {
      'baidu': `https://www.baidu.com/s?wd=${encodedTitle}`,
      'weibo': `https://s.weibo.com/weibo?q=${encodedTitle}`,
      'toutiao': `https://so.toutiao.com/search?keyword=${encodedTitle}`,
      'bilibili': `https://search.bilibili.com/all?keyword=${encodedTitle}`,
      'zhihu': `https://www.zhihu.com/search?q=${encodedTitle}`,
      'douyin': `https://www.douyin.com/search/${encodedTitle}`
    };
    return searchUrls[platform];
  };

  // 获取热搜数据
  const getHotTopics = () => {
    if (!platformData || !platformData.data) {
      return [];
    }
    
    // 根据平台数据结构返回热搜列表
    const data = platformData.data;
    if (Array.isArray(data)) {
      return data.slice(0, 8); // 限制显示8条
    }
    
    // 如果是对象格式，尝试获取热搜列表
    if (data.list && Array.isArray(data.list)) {
      return data.list.slice(0, 8);
    }
    
    if (data.items && Array.isArray(data.items)) {
      return data.items.slice(0, 8);
    }
    
    return [];
  };

  const hotTopics = getHotTopics();

  // 格式化数量显示
  const formatCount = (count) => {
    const numCount = parseFloat(count);
    if (numCount > 0) {
      return (numCount / 10000).toFixed(1) + '万';
    }
    return '';
  };

  // 渲染内容区域
  const renderContent = () => {
    if (loading) {
      return (
        <div className="hot-search-content__loading">
          <div className="hot-search-content__loading-spinner"></div>
          <div className="hot-search-content__loading-text">加载中...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="hot-search-content__error"
        onClick={(e) => {
          // 阻止事件冒泡到 react-grid-layout
          e.stopPropagation();
        }}
        >
          <div className="hot-search-content__error-text">加载失败: {error}</div>
          <button 
            className="hot-search-content__error-retry"
            onClick={(e) => {
              console.log('错误按钮被点击了！', { error, activePlatform });
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              dispatch(fetchHotSearchByPlatform({ platform: activePlatform, refresh: true }));
            }}
            onMouseDown={(e) => {
              console.log('错误按钮 onMouseDown 触发');
              e.stopPropagation();
            }}
            onMouseUp={(e) => {
              console.log('错误按钮 onMouseUp 触发');
              e.stopPropagation();
            }}
            style={{
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 9999,
              cursor: 'pointer'
            }}
          >
            重试
          </button>
        </div>
      );
    }

    if (hotTopics.length === 0) {
      return (
        <div className="hot-search-content__empty">
          <div className="hot-search-content__empty-text">暂无数据</div>
        </div>
      );
    }

    return (
      <>
        {hotTopics.map((topic, index) => (
          <div 
            key={index} 
            className="hot-search-content__item"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleTopicClick(e, topic);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            onWheel={(e) => {
              // 阻止滚动事件冒泡到外部
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // 阻止触摸滚动事件冒泡到外部
              e.stopPropagation();
            }}
            style={{ 
              cursor: (topic.url || topic.link || topic.href) ? 'pointer' : 'default',
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 10
            }}
          >
            <span className="hot-search-content__number">{index + 1}</span>
            <span className="hot-search-content__title">{topic.title || topic.name || topic.text}</span>
            <span className="hot-search-content__count">
              {formatCount(topic.count || topic.hot || topic.value || 0)}
            </span>
          </div>
        ))}
      </>
    );
  };

  return (
    <div 
      className="hot-search-content__area"
      onClick={(e) => {
        // 阻止事件冒泡到 react-grid-layout
        e.stopPropagation();
      }}
      onWheel={(e) => {
        // 阻止滚动事件冒泡到外部
        e.stopPropagation();
      }}
      onTouchMove={(e) => {
        // 阻止触摸滚动事件冒泡到外部
        e.stopPropagation();
      }}
    >
      <div className="hot-search-content__tabs">
        {Object.entries(platformMap).map(([key, name]) => (
          <button
            key={key}
            className={`hot-search-content__tab ${activePlatform === key ? 'hot-search-content__tab--active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePlatformChange(e, key);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            style={{ 
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 10
            }}
          >
            {name}
          </button>
        ))}
      </div>
      
      <div 
        className="hot-search-content__list"
        onWheel={(e) => {
          // 阻止滚动事件冒泡到外部
          e.stopPropagation();
          e.preventDefault();
        }}
        onScroll={(e) => {
          // 阻止滚动事件冒泡到外部
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          // 阻止触摸滚动事件冒泡到外部
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // 阻止触摸开始事件冒泡到外部
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          // 阻止触摸结束事件冒泡到外部
          e.stopPropagation();
        }}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default HotSearchContent; 